import { AnalyticsProperties } from '@play-co/gcinstant';
import { createCardShareCreative } from 'src/creatives/cards';
import type { CreativeAsset } from 'src/creatives/core';
import { getCreativeText, type CreativeType } from 'src/creatives/text';
import { makePayload } from 'src/lib/analytics';
import { GCInstant as platform } from 'src/lib/gcinstant';
import i18n from 'src/lib/i18n/i18n';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { captureGenericError } from 'src/lib/sentry';
import {
  generateInviteURL,
  isShareTargetPickerAvailable,
  openShareTargetPicker,
  sendInviteURL,
  type CardShareKind,
} from 'src/lib/sharing';
import { withLoading } from 'src/lib/utils';
import { track } from 'src/lib/analytics/utils';
import { Feature } from 'src/replicant/analytics';
import type { CardId } from 'src/replicant/cards/cards.ruleset';
import { cardsCreateShareTradeId } from 'src/replicant/cards/cards.getters';
import { inviteMessageWithCTA } from 'src/replicant/chatbot/flex/invite';
import type { CreativeText } from 'src/replicant/creatives/text';
import StateObserver, { now, user } from 'src/StateObserver';
import {
  cardsGetAnalyticsContext,
  cardsGetCardAnalyticsProperties,
  cardsRequestSubFeatureByMethod,
  type CardsDeliveryMethod,
} from 'src/replicant/cards/cards.analytics';

/**
 * Uploaded creative data required to publish a card share.
 */
type CardsShareCreative = {
  creativeAsset: CreativeAsset;
  creativeText: CreativeText;
  userAsset: true;
};

/**
 * Share surfaces presented by the cards sharing UI.
 */
type CardsSharePreferredMode = 'line' | 'others';

/**
 * Card and preferred share surface selected by the player.
 */
type CardsShareOptions = {
  cardId: CardId;
  preferredMode: CardsSharePreferredMode;
};

/**
 * Configuration shared by outbound card shares and incoming share links.
 */
type CardsShareKindConfig = {
  subFeature: string;
  creativeTextType: CreativeType;
  responseTitleKey: string;
  expiredMessageKey: string;
};

/**
 * Card-share metadata keyed by the request, donate, and trade share kinds.
 */
export const CARD_SHARE_CONFIG = {
  request: {
    subFeature: Feature.CARDS.REQUEST,
    creativeTextType: 'card_share_request',
    responseTitleKey: 'cards.responsePopup.title',
    expiredMessageKey: 'cards.responsePopup.messageRequestExpired',
  },
  donate: {
    subFeature: Feature.CARDS.DONATE,
    creativeTextType: 'card_share_donate',
    responseTitleKey: 'cards.responsePopup.donateTitle',
    expiredMessageKey: 'cards.responsePopup.messageDonateExpired',
  },
  trade: {
    subFeature: Feature.CARDS.TRADE,
    creativeTextType: 'card_share_trade',
    responseTitleKey: 'cards.responsePopup.tradeTitle',
    expiredMessageKey: 'cards.responsePopup.messageTradeExpired',
  },
} as const satisfies Record<CardShareKind, CardsShareKindConfig>;

/**
 * Supported card-share kinds derived from the canonical configuration.
 */
export const CARD_SHARE_KINDS = Object.keys(
  CARD_SHARE_CONFIG,
) as CardShareKind[];

/**
 * Build the entry payload shared by LINE and invite URL card shares.
 *
 * @param cardId Card included in the share.
 * @param cardShareKind Request, donate, or trade flow being shared.
 * @param tradeId Stable identifier carried by a trade share link.
 * @returns Payload fields required to recognize and process the share link.
 */
function cardsCreateSharePayload(
  cardId: CardId,
  cardShareKind: CardShareKind,
  deliveryMethod: Exclude<CardsDeliveryMethod, 'request'>,
  tradeId?: string,
) {
  // Resolve analytics metadata from the canonical share configuration.
  const shareConfig = CARD_SHARE_CONFIG[cardShareKind];
  const subFeature =
    cardShareKind === 'request'
      ? cardsRequestSubFeatureByMethod[deliveryMethod]
      : shareConfig.subFeature;

  // Return the complete payload with its creation time for expiry checks.
  return {
    feature: Feature.CARDS._,
    $subFeature: subFeature,
    cardId,
    cardShareKind,
    cardShareMethod: deliveryMethod,
    cardShareCreatedAt: now(),
    tradeId,
  };
}

/**
 * Share a card request through the selected share surface.
 *
 * @param options Card and preferred surface for the request.
 * @returns A promise that resolves after sharing has been handled.
 */
export async function cardsShareRequest(
  options: CardsShareOptions,
): Promise<void> {
  // Start the request-specific card share flow.
  await cardsShareCard('request', options);
}

/**
 * Share a card donation through the selected share surface.
 *
 * @param options Card and preferred surface for the donation.
 * @returns A promise that resolves after sharing has been handled.
 */
export async function cardsShareDonate(
  options: CardsShareOptions,
): Promise<void> {
  // Start the donation-specific card share flow.
  await cardsShareCard('donate', options);
}

/**
 * Share a card trade through the selected share surface.
 *
 * @param options Card and preferred surface for the trade.
 * @returns A promise that resolves after sharing has been handled.
 */
export async function cardsShareTrade(
  options: CardsShareOptions,
): Promise<void> {
  // Start the trade-specific card share flow.
  await cardsShareCard('trade', options);
}

/**
 * Route a card share to LINE when requested and available, or to URL sharing.
 *
 * @param cardShareKind Request, donate, or trade flow being shared.
 * @param options Card and preferred surface selected by the player.
 * @returns A promise that resolves after the selected share flow finishes.
 */
async function cardsShareCard(
  cardShareKind: CardShareKind,
  options: CardsShareOptions,
): Promise<void> {
  // Resolve analytics metadata once for the loading state and share flow.
  const shareConfig = CARD_SHARE_CONFIG[cardShareKind];

  // Keep creative generation and platform sharing covered by the loading UI.
  await withLoading(
    async () => {
      // Trade links need a stable ID before their outbound event and payload are created.
      const tradeId =
        cardShareKind === 'trade'
          ? cardsCreateShareTradeId(user(), now())
          : undefined;

      // Use LINE only when it is both selected and supported by the platform.
      const canShareWithLine =
        options.preferredMode === 'line' && isShareTargetPickerAvailable();
      if (canShareWithLine) {
        await cardsShareLine(options.cardId, cardShareKind, tradeId);
        return;
      }

      // Fall back to native share, LINE URL share, or clipboard handling.
      await cardsShareOthers(options.cardId, cardShareKind, tradeId);
    },
    {
      feature: Feature.CARDS._,
      subFeature: shareConfig.subFeature,
    },
  );
}

/**
 * Build a LINE flex message for a card share and open the target picker.
 *
 * @param cardId Card included in the share payload.
 * @param cardShareKind Request, donate, or trade flow being shared.
 * @param tradeId Stable identifier carried by a trade share link.
 * @returns A promise that resolves after the LINE target picker closes.
 */
async function cardsShareLine(
  cardId: CardId,
  cardShareKind: CardShareKind,
  tradeId?: string,
): Promise<void> {
  // Generate and upload the dynamic card creative before building the message.
  const creative = await cardsGenerateShareCreative(cardId, cardShareKind);
  if (!creative) {
    await openCardShareErrorPopup();
    return;
  }

  // Create the analytics and entry payload consumed by the receiving client.
  const { creativeAsset, creativeText } = creative;
  const sharePayload = cardsCreateSharePayload(
    cardId,
    cardShareKind,
    'line_share',
    tradeId,
  );
  const { encodedPayload, analyticsEventProperties } =
    platform.generateEntryPayload({
      ...makePayload('SHARE'),
      channel: 'SHARE' as AnalyticsProperties.Channel,
      ...sharePayload,
      subFeature: sharePayload.$subFeature,
      $creativeAssetID: creativeAsset.id,
      $creativeTextID: creativeText.id,
      $creativeCTA: creativeText.cta,
      creativeCTA: creativeText.cta,
      webReferrer: user().id,
    });

  // Build the LINE flex message with the uploaded user creative.
  const title = process.env.LINE_APP_NAME || 'ぶるぶるどーぶつ';
  const flexMessage = inviteMessageWithCTA({
    title,
    asset: creativeAsset,
    creative: creativeText,
    heroAspectRatio: '520:289',
    payload: {
      ...encodedPayload,
      alternativeWebIABBucket: 'enabled_mini_app' as const,
    },
  });

  // Open the LINE share target picker with its analytics properties.
  await openShareTargetPicker(flexMessage, analyticsEventProperties);

  // Trade shares must retain the ID carried by their outbound link.
  if (cardShareKind === 'trade' && !tradeId) {
    throw new Error('Trade shares require a trade ID.');
  }

  // Donation links are counted when the sender hands the link to LINE.
  if (cardShareKind === 'donate') {
    track('CardSend', {
      cardID: cardId,
      sendMethod: 'line_share',
      ...cardsGetCardAnalyticsProperties(cardId),
      ...cardsGetAnalyticsContext(user()),
    });
  }

  // Request analytics are recorded after the target picker closes.
  if (cardShareKind === 'request') {
    track('CardRequest', {
      cardID: cardId,
      requestMethod: 'line_share',
      ...cardsGetCardAnalyticsProperties(cardId),
      ...cardsGetAnalyticsContext(user()),
    });
  }

  // Trade links are counted once before the recipient selects a counter card.
  if (cardShareKind === 'trade' && tradeId) {
    track('CardTradeSend', {
      tradeID: tradeId,
      offerCardID: cardId,
      tradeMethod: 'line_share',
      ...cardsGetAnalyticsContext(user()),
    });
  }
}

/**
 * Generate a card share URL and send it through non-target-picker sharing.
 *
 * @param cardId Card included in the share payload.
 * @param cardShareKind Request, donate, or trade flow being shared.
 * @param tradeId Stable identifier carried by a trade share link.
 * @returns A promise that resolves after URL sharing has been handled.
 */
async function cardsShareOthers(
  cardId: CardId,
  cardShareKind: CardShareKind,
  tradeId?: string,
): Promise<void> {
  // Generate the dynamic creative and short invite URL.
  const shareData = await cardsGenerateShareURL(cardId, cardShareKind, tradeId);
  if (!shareData) {
    await openCardShareErrorPopup();
    return;
  }

  // Trade shares must retain the ID carried by their outbound link.
  if (cardShareKind === 'trade' && !tradeId) {
    throw new Error('Trade shares require a trade ID.');
  }

  // All card links confirm when the URL is copied to the clipboard.
  const inviteMethod = await sendInviteURL({
    ...shareData,
    fallbackPopupOpts: {
      title: i18n('social.invite.referral.copiedUrl'),
      message: i18n('social.invite.referral.copiedUrlMessage'),
    },
  });
  const shareMethod = inviteMethod === 'copy' ? 'copy' : 'native_share';

  // Donation links are counted when the sender hands the link to URL sharing.
  if (cardShareKind === 'donate') {
    track('CardSend', {
      cardID: cardId,
      sendMethod: shareMethod,
      ...cardsGetCardAnalyticsProperties(cardId),
      ...cardsGetAnalyticsContext(user()),
    });
  }

  // Clipboard is sender-only; all delivered request URLs use native attribution.
  if (cardShareKind === 'request') {
    track('CardRequest', {
      cardID: cardId,
      requestMethod: shareMethod,
      ...cardsGetCardAnalyticsProperties(cardId),
      ...cardsGetAnalyticsContext(user()),
    });
  }

  // Trade links are counted once before the recipient selects a counter card.
  if (cardShareKind === 'trade' && tradeId) {
    track('CardTradeSend', {
      tradeID: tradeId,
      offerCardID: cardId,
      tradeMethod: shareMethod,
      ...cardsGetAnalyticsContext(user()),
    });
  }
}

/**
 * Generate invite URL metadata for a card share.
 *
 * @param cardId Card included in the share payload.
 * @param cardShareKind Request, donate, or trade flow encoded in the payload.
 * @param tradeId Stable identifier carried by a trade share link.
 * @returns Invite URL metadata, or null when the creative cannot be generated.
 */
async function cardsGenerateShareURL(
  cardId: CardId,
  cardShareKind: CardShareKind,
  tradeId?: string,
) {
  // Generate and upload the dynamic card creative used by the invite URL.
  const creative = await cardsGenerateShareCreative(cardId, cardShareKind);
  if (!creative) {
    return null;
  }

  // Generate a cached or new short URL containing the card share payload.
  return generateInviteURL({
    ...creative,
    data: cardsCreateSharePayload(
      cardId,
      cardShareKind,
      'native_share',
      tradeId,
    ),
  });
}

/**
 * Render and upload the dynamic creative used by a card share.
 *
 * @param cardId Card rendered in the creative.
 * @param cardShareKind Request, donate, or trade visual treatment to render.
 * @returns Uploaded creative metadata, or null when rendering or upload fails.
 */
async function cardsGenerateShareCreative(
  cardId: CardId,
  cardShareKind: CardShareKind,
): Promise<CardsShareCreative | null> {
  // Resolve localized copy and a deterministic user asset identifier.
  const shareConfig = CARD_SHARE_CONFIG[cardShareKind];
  const creativeText = getCreativeText(shareConfig.creativeTextType);
  const creativeAssetId = `cards_${cardShareKind}_${cardId}_${creativeText.id}`;

  try {
    // Render the card-specific creative with its localized text and CTA.
    const creative = await createCardShareCreative({
      kind: cardShareKind,
      cardId,
      cta: creativeText.cta,
    });

    // Upload the rendered image so both sharing channels can access it.
    const uploadedAssetId = await StateObserver.replicant.uploadUserAsset(
      creative.image,
      creativeAssetId,
    );

    // Return the complete creative metadata expected by the sharing APIs.
    return {
      creativeAsset: {
        id: creativeAssetId,
        image: StateObserver.replicant.getUserAssetUrl(uploadedAssetId),
      },
      creativeText,
      userAsset: true,
    };
  } catch (error) {
    // Capture rendering or upload failures with enough context to investigate.
    captureGenericError('generateCardShareCreativeFailed', error, {
      extra: { cardId, cardShareKind },
    });
    return null;
  }
}

/**
 * Show the generic retry popup when a card share cannot be created.
 *
 * @returns A promise that resolves after the player closes the popup.
 */
async function openCardShareErrorPopup(): Promise<void> {
  // Explain that the share failed without exposing implementation details.
  await openPopupPromise('popupError', {
    message: i18n('error.general.tryAgain'),
  });
}
